import { Box, Flex, Text } from '@chakra-ui/react'
import {
  Banner,
  Button,
  InputSelectControl,
  ModalContextProvider,
  TextAreaInput
} from '@sequencehq/core-components'
import {
  loadableExtractionResultAtom,
  modelAtom,
  promptAtom
} from 'ContractExtraction/atoms'
import { defaultPrompt } from 'ContractExtraction/prompt'
import { Model } from 'ContractExtraction/types'
import { useAtom } from 'jotai'
import { ExtractionDebugDialog } from './ExtractionDebugDialog'
import { useState } from 'react'
import { getSequenceRequestId } from 'ContractExtraction/utils/getSequenceRequestId'
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid'

const modelOptions = Object.values(Model).map(model => ({
  label: model,
  value: model
}))

export const ModelConfig = () => {
  const [model, setModel] = useAtom(modelAtom)
  const [prompt, setPrompt] = useAtom(promptAtom)

  const [showExtractionDebugDialog, setShowExtractionDebugDialog] =
    useState(false)
  const [extractionResult] = useAtom(loadableExtractionResultAtom)
  const sequenceRequestId = getSequenceRequestId(extractionResult)

  const handleLoadPrompt = () => {
    if (prompt) {
      const confirmed = confirm(
        'This will override the current prompt. Are you sure?'
      )
      if (!confirmed) {
        return
      }
    }

    setPrompt(defaultPrompt)
  }

  return (
    <Flex gap={4} direction="column">
      <Banner variant="neutral">
        This configuration will be persisted in local storage. Any values that
        aren't set will default to whatever settings the backend has.
      </Banner>
      <Box>
        <Text mb={1}>Model</Text>
        <InputSelectControl
          options={[
            ...modelOptions,
            { label: 'Default backend model', value: '' }
          ]}
          initialValue={model ?? ''}
          onChange={value => {
            setModel(value as Model)
          }}
        />
      </Box>

      <Box>
        <Flex alignItems="center" gap={2} mb={2} justifyContent="space-between">
          <Text>Prompt</Text>
          <Button variant="ghost" onClick={handleLoadPrompt}>
            Prefill
          </Button>
        </Flex>

        <TextAreaInput
          value={prompt ?? ''}
          placeholder="Default backend prompt"
          onChange={value => {
            setPrompt(value)
          }}
        />
      </Box>

      <Button
        variant="destructive"
        onClick={() => {
          setModel(undefined)
          setPrompt(undefined)
        }}
      >
        Clear all
      </Button>

      {sequenceRequestId && (
        <>
          <Button
            onClick={() => setShowExtractionDebugDialog(true)}
            leadingIcon={<MagnifyingGlassIcon />}
          >
            Debug last extraction
          </Button>
          <ModalContextProvider isOpen={showExtractionDebugDialog}>
            <ExtractionDebugDialog
              extractionResult={extractionResult}
              setShowDebugModal={setShowExtractionDebugDialog}
            />
          </ModalContextProvider>
        </>
      )}
    </Flex>
  )
}
